import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { User, Calendar, Brain, FileText, Sparkles } from '../../../components/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { isUserCoach, checkCoachAIAccess } from '../../../lib/auth';
import { ResultsGraph } from '../../../components/ResultsGraph';
import { HarmonicGraph } from '../../../components/HarmonicGraph';
import { AIAnalysisPanel } from '../../../components/ai/AIAnalysisPanel';
import { CoachNotesPanel } from '../../../components/ai-coach/CoachNotesPanel';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import type { AssessmentResult } from '../../../types/results';

export function RespondentResultsTR() {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [results, setResults] = useState<AssessmentResult[]>([]);
  const [selectedVersion, setSelectedVersion] = useState(0);
  const [respondentName, setRespondentName] = useState('');
  const [hasAccess, setHasAccess] = useState(false);
  const [hasAIAccess, setHasAIAccess] = useState(false);
  const [coachId, setCoachId] = useState<string | null>(null);
  const [respondentId, setRespondentId] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !userId) {
      navigate('/tr/login');
      return;
    }

    const init = async () => {
      try {
        const isCoach = await isUserCoach(user.id);
        if (!isCoach) {
          navigate('/tr/login');
          return;
        }

        const { data: coach } = await supabase
          .from('coaches')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!coach) {
          navigate('/tr/login');
          return;
        }

        const { data: respondent } = await supabase
          .from('respondents')
          .select('id, user_id, users!respondents_user_id_fkey(full_name, email)')
          .eq('user_id', userId)
          .eq('coach_id', coach.id)
          .maybeSingle();

        if (!respondent) {
          navigate('/tr/coach/respondents');
          return;
        }

        setRespondentName((respondent.users as any)?.full_name || (respondent.users as any)?.email || 'Katilimci');
        setHasAccess(true);
        setCoachId(coach.id);
        setRespondentId(respondent.id);

        const aiAccess = await checkCoachAIAccess(user.id);
        setHasAIAccess(aiAccess);

        const { data: resultsData } = await supabase
          .from('results')
          .select('*')
          .eq('user_id', userId)
          .order('completed_at', { ascending: false });

        const mapped = (resultsData || []).map(r => ({
          id: r.id,
          userId: r.user_id,
          coachId: r.coach_id || undefined,
          partnerId: r.partner_id || undefined,
          scores: r.scores as any,
          harmonicScores: r.harmonic_scores as any,
          completedAt: new Date(r.completed_at || Date.now()),
          aiAnalysis: r.ai_analysis || undefined,
          coachAiAnalysis: r.coach_ai_analysis || undefined,
          version: r.version || 1,
          originalResultId: r.original_result_id || undefined
        })) as AssessmentResult[];

        setResults(mapped);
      } catch (err) {
        console.error('Error loading results:', err);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate, userId]);

  const handleAnalysisUpdate = (analysis: string) => {
    setResults(prev => prev.map((r, i) =>
      i === selectedVersion ? { ...r, coachAiAnalysis: analysis } : r
    ));
  };

  if (authLoading || isLoading) {
    return <LoadingSpinner message="Sonuclar yukleniyor..." />;
  }

  if (!hasAccess || results.length === 0) {
    return (
      <div className="min-h-screen freud-bg py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="freud-card-elevated p-8">
            <FileText className="w-16 h-16 text-cream-400 mx-auto mb-4" />
            <h2 className="freud-heading-xl text-chocolate-900 mb-2">Sonuc bulunamadi</h2>
            <p className="text-chocolate-500 mb-6">
              Bu katilimci henuz bir degerlendirme tamamlamadi.
            </p>
            <Link
              to="/tr/coach/respondents"
              className="freud-btn-primary inline-flex items-center gap-2"
            >
              Katilimcilara Don
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const selectedResult = results[selectedVersion];

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-sage-100 rounded-xl flex items-center justify-center">
                <User className="w-6 h-6 text-sage-600" />
              </div>
              <div>
                <h1 className="freud-heading-xl text-chocolate-900">{respondentName}</h1>
                <div className="flex items-center gap-2 text-sm text-chocolate-500">
                  <Calendar className="w-4 h-4" />
                  Tamamlandi: {selectedResult.completedAt.toLocaleDateString('tr-TR')}
                </div>
              </div>
            </div>

            {results.length > 1 && (
              <select
                value={selectedVersion}
                onChange={(e) => setSelectedVersion(Number(e.target.value))}
                className="freud-input w-auto"
              >
                {results.map((_, index) => (
                  <option key={index} value={index}>
                    Surum {results.length - index}
                    {index === 0 ? ' (En Son)' : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8 space-y-8">
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="freud-card-elevated p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-sage-100 rounded-xl flex items-center justify-center">
                <Brain className="w-5 h-5 text-sage-600" />
              </div>
              <h2 className="freud-heading-xl text-chocolate-900">
                Karakteristik Profil
              </h2>
            </div>
            <ResultsGraph scores={selectedResult.scores} />
          </div>

          <div className="freud-card-elevated p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-coral-100 rounded-xl flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-coral-600" />
              </div>
              <h2 className="freud-heading-xl text-chocolate-900">
                Harmonik Olcek Profili
              </h2>
            </div>
            <HarmonicGraph scores={selectedResult.harmonicScores} />
          </div>
        </div>

        {hasAIAccess && (
          <AIAnalysisPanel
            analysis={selectedResult.coachAiAnalysis}
            scores={selectedResult.scores}
            harmonicScores={selectedResult.harmonicScores}
            resultId={selectedResult.id}
            showRegenerate
            respondentName={respondentName}
            storageField="coach_ai_analysis"
            onAnalysisUpdate={handleAnalysisUpdate}
          />
        )}

        {coachId && respondentId && (
          <CoachNotesPanel coachId={coachId} respondentId={respondentId} />
        )}
      </main>
    </div>
  );
}
